/**
 * Browser error tracing — self-hosted Sentry (docs/sentry.md).
 *
 * The DSN arrives from `GET /client-config` rather than from the bundle: the
 * SPA is served by the API, so one `.env.prod` configures both halves and
 * turning tracing on is a restart, not a rebuild. Nothing is imported eagerly —
 * an install without a DSN never downloads the SDK.
 */
import type { ErrorInfo } from 'react';
// Type-only: the runtime import below is dynamic, so an install with no DSN
// never downloads the SDK.
import type * as SentryReact from '@sentry/react';

interface ClientConfig {
  sentry: {
    dsn: string;
    environment: string;
    release?: string;
    tracesSampleRate: number;
  } | null;
}

type SentryModule = typeof SentryReact;

let sentry: SentryModule | null = null;

/**
 * Start tracing if this install has a DSN. Resolves either way, and never
 * rejects: a monitoring tool that can stop the app from starting is worse
 * than no monitoring tool.
 */
export async function startErrorTracing(): Promise<void> {
  try {
    const response = await fetch('/api/v1/client-config', { credentials: 'same-origin' });
    if (!response.ok) return;
    const config = (await response.json()) as ClientConfig;
    if (!config.sentry) return;

    const module = await import('@sentry/react');
    module.init({
      dsn: config.sentry.dsn,
      environment: config.sentry.environment,
      release: config.sentry.release,
      sendDefaultPii: false,
      tracesSampleRate: config.sentry.tracesSampleRate,
      integrations: [module.browserTracingIntegration()],
      // Same origin only: the API is the only thing this app calls, and a
      // trace header on a third-party request would leak our trace ids.
      tracePropagationTargets: [window.location.origin],
      beforeSend(event) {
        // What someone typed is never the evidence a stack trace needs.
        if (event.request?.data) delete event.request.data;
        return event;
      },
    });
    sentry = module;
  } catch {
    // Offline, blocked, or misconfigured. The app does not care.
  }
}

/** Report a React render failure. A no-op when tracing is not configured. */
export function reportRenderError(error: unknown, info: ErrorInfo): void {
  sentry?.captureException(error, {
    extra: { componentStack: info.componentStack ?? undefined },
  });
}

/** Report anything else worth knowing about. A no-op when unconfigured. */
export function reportError(error: unknown, context?: Record<string, unknown>): void {
  sentry?.captureException(error, context ? { extra: context } : undefined);
}
